import type { Editor } from '@tiptap/react';
import { useEditor } from '@tiptap/react';

import { EditorContent } from '@tiptap/react';
import { getTableBlockExtensionsForPreviewContent, getTextBlockExtensionsForPreview } from '../helpers/config';
import { isSelectionWithinSingleBlock, WRAPPER_CLASS, SELECTION_CHECK_DELAY } from '../helpers/previewSelection';
import type { PreviewVariablesContextType } from '../../../../../providers/PreviewVariablesContext.tsx';
import { isJson } from '../../../../../utils/isJson.ts';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';
import { useMultiEditor } from '../MultiEditorContext.tsx';
import { useActiveEditor } from '../ActiveEditorContext';
import type { CurrencySettings } from '../../../../../services/documents/DocumentRepository.ts';
import { DOLLAR_SIGN } from '../../Block/Table/defaultData.tsx';

export interface TiptapEditorProps {
  blockId: string;
  documentContent: string;
  isTemplate?: boolean;
  usePreviewVariables: () => PreviewVariablesContextType;
  style: CSSProperties;
  currencySettings?: CurrencySettings;
}

const TipTapTextblockPreview = ({
  isTemplate,
  documentContent,
  blockId,
  usePreviewVariables,
  style,
  currencySettings,
}: TiptapEditorProps) => {
  const editorId = `tiptap_editor_${blockId}`;

  const { variables } = usePreviewVariables();
  const { registerEditor, unregisterEditor } = useMultiEditor();
  const { setActiveEditor } = useActiveEditor();

  const initialDocumentContent = isJson(documentContent) ? JSON.parse(documentContent) : documentContent;

  const editor = useEditor(
    {
      content: initialDocumentContent ?? '',
      editable: false,
      editorProps: {
        attributes: {
          class: 'tiptap-textblock-editor tiptap-textblock-editor-preview',
          currencySign: currencySettings?.currencySign || DOLLAR_SIGN,
        },
      },
      extensions: isTemplate
        ? getTableBlockExtensionsForPreviewContent({ variables: variables ?? [] })
        : getTextBlockExtensionsForPreview({ variables: variables ?? [] }),
      onCreate: ({ editor }: { editor: Editor }) => {
        editor.storage.blockId = blockId;
      },
      onSelectionUpdate: ({ editor }: { editor: Editor }) => {
        if (!editor.state.selection.empty && !isTemplate) {
          setActiveEditor(editor);
        }
      },
    },
    [documentContent, isTemplate]
  );

  useEffect(() => {
    if (editor && !isTemplate) {
      registerEditor(editorId, editor);
      return () => {
        unregisterEditor(editorId);
      };
    }
  }, [editor, editorId, registerEditor, unregisterEditor, isTemplate]);

  useEffect(() => {
    if (!editor || isTemplate) return;

    const wrapperElement = editor.view.dom.closest(`.${WRAPPER_CLASS}`) as Element | null;
    if (!wrapperElement) return;

    const handleDocumentMouseUp = (event: MouseEvent) => {
      const target = event.target as Node;
      const selection = window.getSelection();

      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

      const range = selection.getRangeAt(0);
      const isRelevantToThisBlock =
        wrapperElement.contains(target) || wrapperElement.contains(range.startContainer) || wrapperElement.contains(range.endContainer);

      if (!isRelevantToThisBlock) return;

      setTimeout(() => {
        if (!editor) return;

        const currentSelection = window.getSelection();
        if (!currentSelection || currentSelection.rangeCount === 0 || currentSelection.isCollapsed) return;

        if (!isSelectionWithinSingleBlock(currentSelection)) {
          currentSelection.removeAllRanges();
          if (editor.commands.clearSelection) {
            editor.commands.clearSelection();
          }
        }
      }, SELECTION_CHECK_DELAY);
    };

    document.addEventListener('mouseup', handleDocumentMouseUp, true);

    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp, true);
    };
  }, [editor, isTemplate]);

  return editor ? (
    <EditorContent data-testid={`test_${editorId}`} className={`tiptap-textblock-wrapper ${editorId}`} editor={editor} style={style} />
  ) : null;
};

export default TipTapTextblockPreview;
